import React, { useState, useEffect, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import { getIgnoredDomains, addIgnoredDomain, deleteIgnoredDomain } from '../../../api/url_data';
import { AddIgnoredDomain } from './AddIgnoredDomain';
import { IgnoredDomainsList } from './IgnoredDomainsList';
import './IgnoredDomainsModal.scss';

export const IgnoredDomainsModal = ({ isOpen, onClose }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastAddedDomainId, setLastAddedDomainId] = useState(null);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const response = await getIgnoredDomains();
      const sortedDomains = (response.data || []).sort((a, b) => a.domain.localeCompare(b.domain));
      setDomains(sortedDomains);
      setError(null);
    } catch {
      setError('Failed to load ignored domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async domain => {
    try {
      const response = await addIgnoredDomain(domain);
      const newDomain = response.data;

      setDomains(prevDomains => {
        const updated = [...prevDomains, newDomain];
        return updated.sort((a, b) => a.domain.localeCompare(b.domain));
      });

      // Set the newly added domain ID to scroll to it
      setLastAddedDomainId(newDomain.id);

      return true;
    } catch (err) {
      throw new Error(err || 'Failed to add domain');
    }
  };

  const handleDeleteDomain = async id => {
    try {
      await deleteIgnoredDomain(id);

      setDomains(prevDomains => prevDomains.filter(domain => domain.id !== id));
    } catch {
      setError('Failed to delete domain');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadDomains();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className='ignored-domains-modal-overlay' onClick={onClose}>
      <div className='ignored-domains-modal' onClick={e => e.stopPropagation()}>
        <div className='modal-header'>
          <h2>Manage Ignored Domains</h2>
          <button className='close-button' onClick={onClose}>
            <IoClose size={24} />
          </button>
        </div>

        <div className='modal-content'>
          <div className='sticky-form'>
            <AddIgnoredDomain onAdd={handleAddDomain} />
            {error && <div className='error-message'>{error}</div>}
          </div>

          <div className='scrollable-list'>
            <IgnoredDomainsList
              domains={domains}
              loading={loading}
              onDelete={handleDeleteDomain}
              lastAddedDomainId={lastAddedDomainId}
              onScrollComplete={() => setLastAddedDomainId(null)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
